import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IssueType, IssueWithChildren, IssueStatus } from '@momentum/types';
import { useCompanyHierarchy } from '@/shared/hooks/useIssues';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { GoalCard } from './goal-card/GoalCard';
import { GoalsTreeEmpty } from './goal-card/components/GoalsTreeEmpty';
import { isRestrictedIssue } from '@/shared/utils/issue-guards';

interface GoalsTreeViewProps {
  selectedEmployeeId?: string;
  showCompleted?: boolean;
  expandedNodes: Set<string>;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const isEmployeeInvolved = (
  goal: IssueWithChildren,
  employeeId: string
): boolean => {
  if (isRestrictedIssue(goal)) return false;

  const isCreator = goal.issueCreator._id === employeeId;
  const isAssignee = goal.assignees.some((a) => a._id === employeeId);
  return isCreator || isAssignee;
};

const findPathsToEmployeeGoals = (
  goals: IssueWithChildren[],
  employeeId: string,
  currentPath: string[] = []
): string[][] => {
  const paths: string[][] = [];

  for (const goal of goals) {
    const newPath = [...currentPath, goal._id];

    if (isEmployeeInvolved(goal, employeeId)) {
      paths.push(newPath);
    }

    // Recursively search in children
    if (goal.children && goal.children.length > 0) {
      const childPaths = findPathsToEmployeeGoals(
        goal.children,
        employeeId,
        newPath
      );
      paths.push(...childPaths);
    }
  }

  return paths;
};

// Get all node IDs that should be expanded (all nodes in paths to employee goals)
const getNodesToExpand = (paths: string[][]): Set<string> => {
  const nodesToExpand = new Set<string>();

  for (const path of paths) {
    for (const nodeId of path) {
      nodesToExpand.add(nodeId);
    }
  }

  return nodesToExpand;
};

// Get all goal IDs where employee is directly involved (target goals)
const getTargetGoalIds = (
  goals: IssueWithChildren[],
  employeeId: string
): Set<string> => {
  const targetIds = new Set<string>();

  const traverse = (goalList: IssueWithChildren[]) => {
    for (const goal of goalList) {
      if (isEmployeeInvolved(goal, employeeId)) {
        targetIds.add(goal._id);
      }

      if (goal.children && goal.children.length > 0) {
        traverse(goal.children);
      }
    }
  };

  traverse(goals);
  return targetIds;
};

const buildPathGoalIds = (paths: string[][]): Set<string> => {
  const pathGoalIds = new Set<string>();

  for (const path of paths) {
    for (const goalId of path) {
      pathGoalIds.add(goalId);
    }
  }

  return pathGoalIds;
};

export const GoalsTreeView: React.FC<GoalsTreeViewProps> = ({
  selectedEmployeeId,
  showCompleted = true,
  expandedNodes,
  setExpandedNodes,
}) => {
  const { t } = useTranslation('goals');
  const employee = useEmployeeContext();

  const hierarchyParams = useMemo(
    () => ({
      issueTypes: [IssueType.GOAL, IssueType.TASK],
    }),
    []
  );

  const { data, isLoading, error } = useCompanyHierarchy(hierarchyParams);

  const goals = useMemo(() => data ?? [], [data]);

  // Calculate paths, target goals, and path goal IDs when employee is selected
  const { pathGoalIds, targetGoalIds } = useMemo(() => {
    if (!selectedEmployeeId || goals.length === 0) {
      return {
        pathGoalIds: new Set<string>(),
        targetGoalIds: new Set<string>(),
      };
    }

    const employeePaths = findPathsToEmployeeGoals(goals, selectedEmployeeId);
    const targets = getTargetGoalIds(goals, selectedEmployeeId);
    const pathIds = buildPathGoalIds(employeePaths);

    return {
      pathGoalIds: pathIds,
      targetGoalIds: targets,
    };
  }, [selectedEmployeeId, goals]);

  // Auto-expand paths when employee is selected
  useEffect(() => {
    if (selectedEmployeeId && goals.length > 0) {
      const paths = findPathsToEmployeeGoals(goals, selectedEmployeeId);
      const nodesToExpand = getNodesToExpand(paths);
      setExpandedNodes(nodesToExpand);
    }
  }, [selectedEmployeeId, goals]);

  const toggleNode = (goalId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(goalId)) {
        newSet.delete(goalId);
      } else {
        newSet.add(goalId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <GoalsTreeEmpty message={t('loadingGoals')} variant="loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <GoalsTreeEmpty
          message={t('errorLoadingGoals')(error.message)}
          variant="error"
        />
      </div>
    );
  }

  return (
    <div>
      {goals?.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <GoalsTreeEmpty message={t('noGoalsFound')} variant="empty" />
        </div>
      ) : (
        <div className="space-y-3">
          {goals
            ?.filter((goal) => {
              // Filter out completed goals if showCompleted is false
              if (
                !showCompleted &&
                !isRestrictedIssue(goal) &&
                goal.status === IssueStatus.COMPLETED
              ) {
                return false;
              }
              // If no employee is selected, show all goals
              if (!selectedEmployeeId) return true;
              // Only show goals that are in a path to employee's goals
              return pathGoalIds.has(goal._id);
            })
            .map((goal) => {
              const isTarget = selectedEmployeeId
                ? targetGoalIds.has(goal._id)
                : false;

              return (
                <GoalCard
                  key={goal._id}
                  goal={goal}
                  currentEmployeeId={employee._id}
                  level={0}
                  isExpanded={expandedNodes.has(goal._id)}
                  onToggle={() => toggleNode(goal._id)}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  selectedEmployeeId={selectedEmployeeId}
                  isTarget={isTarget}
                  pathGoalIds={pathGoalIds}
                  targetGoalIds={targetGoalIds}
                  showCompleted={showCompleted}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};
